export class QuillImagePasteService {
    quill;

    private attachments;
    private isAddingAsAttachment;
    private saveAsAttachment;
    private saveToServer;

    /**
     * Instantiate the module given a quill instance and any options
     */
    constructor(quill: any, options = {}) {
        this.saveToServer = options['saveToServer'];
        this.attachments = options['attachments'];
        this.saveAsAttachment = options['saveAsAttachment'];
        this.isAddingAsAttachment = options['isAddingAsAttachment'];
        // save the quill reference
        this.quill = quill;
        this.quill.uploader.options.handler = () => {};
        // bind handlers to this instance
        this.handleDrop = this.handleDrop.bind(this);
        this.handlePaste = this.handlePaste.bind(this);
        // listen for drop and paste events
        this.quill.root.addEventListener(
            'drop',
            (event) => {
                this.handleDrop(event);
            },
            false,
        );
        this.quill.root.addEventListener(
            'paste',
            (event) => {
                this.handlePaste(event);
            },
            false,
        );
    }

    /**
     * Handler for drop event to read dropped files from evt.dataTransfer
     */
    handleDrop(evt) {
        const insertedFiles = evt.dataTransfer?.files;

        if (insertedFiles && insertedFiles.length) {
            evt.preventDefault();

            if (document.caretRangeFromPoint) {
                const selection = document.getSelection();
                const range = document.caretRangeFromPoint(evt.clientX, evt.clientY);
                if (selection && range) {
                    selection.setBaseAndExtent(
                        range.startContainer,
                        range.startOffset,
                        range.startContainer,
                        range.startOffset,
                    );
                }
            }
            this.readFiles(insertedFiles, this.insert.bind(this));
        }
    }

    /**
     * Handler for paste event to read pasted files from evt.clipboardData
     */
    handlePaste(evt) {
        if (evt.clipboardData && evt.clipboardData.files && evt.clipboardData.files.length) {
            evt.preventDefault();
        }

        if (evt.clipboardData && evt.clipboardData.items && evt.clipboardData.items.length) {
            this.readFiles(evt.clipboardData.items, (dataUrl) => {
                setTimeout(() => {
                    this.insert(dataUrl);
                }, 0);
            });
        }
    }

    /**
     * Insert the image into the document at the current cursor position
     */
    insert(dataUrl) {
        const file = this.dataURLtoFile(dataUrl, 'inline-image');

        if (this.isAddingAsAttachment) {
            this.saveAsAttachment(file);
        } else {
            this.saveToServer(this.quill, file);
        }
    }

    /**
     * Extract image URIs a list of files from evt.dataTransfer or evt.clipboardData
     */
    readFiles(files, callback) {
        // check each file for an image
        [].forEach.call(files, (file) => {
            if (!file.type.match(/^image\/(gif|jpe?g|a?png|svg|webp|bmp|vnd\.microsoft\.icon)/i)) {
                // file is not an image
                // Note that some file formats such as psd start with image/* but are not readable
                return;
            }
            // set up file reader
            const reader = new FileReader();
            reader.onload = (evt) => {
                callback(evt.target['result']);
            };
            // read the clipboard item or file
            const blob = file.getAsFile ? file.getAsFile() : file;
            if (blob instanceof Blob) {
                reader.readAsDataURL(blob);
            }
        });
    }

    private dataURLtoFile(dataurl, filename) {
        const arr = dataurl.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);

        if (this.isAddingAsAttachment) {
            // if the file extension is unknown
            let extension;
            // do something like this
            const lowerCase = bstr.toLowerCase();
            if (lowerCase.indexOf('png') !== -1) {
                extension = 'png';
            } else if (lowerCase.indexOf('jpg') !== -1 || lowerCase.indexOf('jpeg') !== -1) {
                extension = 'jpg';
            } else {
                extension = 'tiff';
            }

            if (extension) {
                filename += '.' + extension;
            }
        }

        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
    }
}
